import React, { useEffect, useState } from "react";
import { useMemberstack } from "@memberstack/react";
import { API_BASE } from "../../config";
import { useNavigate } from "react-router-dom";
import { AlertMessage } from "../../components/MessageAlert";
import { UserCircleIcon, CheckIcon } from "@heroicons/react/24/solid";
import { Input } from "../../components/catalyst/input";
import { Field, Label } from "../../components/catalyst/fieldset";

function toggleArrayValue(prev, value, checked) {
  if (checked) return [...new Set([...prev, value])];
  return prev.filter((v) => v !== value);
}

function OnBoardFicheNotaire() {
  const memberstack = useMemberstack();
  const navigate = useNavigate();

  const [member, setMember] = useState(null);
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [competences, setCompetences] = useState([]);
  const [specialites, setSpecialites] = useState([]);
  const [langues, setLangues] = useState([]);
  const [whatsapp, setWhatsapp] = useState(false);
  const [visio, setVisio] = useState(false);
  const [signature, setSignature] = useState(false);
  const [preview, setPreview] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [erreurZapier, setErreurZapier] = useState(false);
  const [messageErreur, setMessageErreur] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    memberstack.getCurrentMember().then(({ data: m }) => {
      if (!m) {
        navigate("/app/connexion", { replace: true });
        return;
      }

      setMember(m);

      if (m.profileImage) {
        setPreview(m.profileImage);
      }

      setPrenom((prev) => prev || m?.customFields?.["first-name"] || "");
      setNom((prev) => prev || m?.customFields?.["last-name"] || "");
    });
  }, [memberstack, navigate]);

  const onCheckCompetence = (event) => {
    const { checked, value } = event.target;
    setCompetences((prev) => toggleArrayValue(prev, value, checked));
  };

  const onCheckSpecialite = (event) => {
    const { checked, value } = event.target;
    setSpecialites((prev) => toggleArrayValue(prev, value, checked));
  };

  const onCheckLangue = (event) => {
    const { checked, value } = event.target;
    setLangues((prev) => toggleArrayValue(prev, value, checked));
  };

  const onUploadPhoto = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const fileMb = selectedFile.size / 1024 ** 2;
    if (fileMb >= 5) {
      alert("Votre fichier est trop volumineux. Taille maximale 5MB.");
      event.target.value = "";
      return;
    }

    try {
      setUploadingPhoto(true);
      setErreurZapier(false);
      setMessageErreur("");

      setPhotoFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
    } catch (error) {
      console.error("erreur preview photo", error);
      setErreurZapier(true);
      setMessageErreur("Impossible de charger la photo.");
      setPhotoFile(null);
      setPreview("");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting || uploadingPhoto) return;

    if (!member) {
      setErreurZapier(true);
      setMessageErreur("Session expirée. Merci de vous reconnecter.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErreurZapier(false);
      setMessageErreur("");

      const formData = new FormData();
      formData.append("nom", nom);
      formData.append("prenom", prenom);
      formData.append("linkedin", linkedin);
      formData.append("whatsapp", JSON.stringify(whatsapp));
      formData.append("visio", JSON.stringify(visio));
      formData.append("signature", JSON.stringify(signature));
      formData.append("competences", JSON.stringify(competences));
      formData.append("specialites", JSON.stringify(specialites));
      formData.append("langues", JSON.stringify(langues));
      formData.append("member", JSON.stringify(member));

      if (photoFile) {
        formData.append("photo", photoFile);
      }

      //https://cloterenotaire.onrender.com/api/newNotaire2
      //http://localhost:5050/api/newNotaire2
      const response = await fetch(`${API_BASE}/api/newNotaire2`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Erreur serveur");
      }

      navigate("/app/onboard-notaire/abonnement", { replace: true });
    } catch (error) {
      //console.error("erreur newNotaire2", error);
      setErreurZapier(true);
      setMessageErreur(
        error.message || "Oops il semblerait qu'il y ait eu un problème, veuillez recommencer"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="grid min-h-full place-items-center bg-gradient-to-b from-indigo-100 to-pink-100 px-6 py-24 sm:py-24 lg:px-8">
      <div className="text-left mb-5">
        <p className="flex items-center text-md font-bold tracking-tight text-gray-500 sm:text-md">
          <CheckIcon className="size-6" /> Renseignez vos informations personnelles
        </p>
        <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
          Personnalisez votre fiche notaire
        </h1>
      </div>

      <div className="space-y-10 divide-y divide-gray-900/10">
        <div className="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3">
          <div className="px-4 sm:px-0">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Votre profil notaire
            </h2>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2"
          >
            {erreurZapier && <AlertMessage type="error" message={messageErreur} />}

            <div className="px-4 py-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-4">
                  <Field>
                    <Label>Votre nom <sup>*</sup></Label>
                    <Input required value={nom} onChange={(e) => setNom(e.target.value)} />
                  </Field>
                </div>

                <div className="sm:col-span-4">
                  <Field>
                    <Label>Prénom <sup>*</sup></Label>
                    <Input required value={prenom} onChange={(e) => setPrenom(e.target.value)} />
                  </Field>
                </div>

                <div className="sm:col-span-4">
                  <Field>
                    <Label>LinkedIn</Label>
                    <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 sm:max-w-md">
                      <span className="flex select-none items-center pl-3 text-gray-500 sm:text-sm">
                        https://www.linkedin.com/in/
                      </span>
                      <Input value={linkedin} onChange={(e) => setLinkedin(e.target.value)} />
                    </div>
                  </Field>
                </div>

                <div className="col-span-full">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Photo de profil
                  </label>
                  <p className="text-sm leading-6 text-gray-600">
                    Les profils notaire avec photo sont toujours plus appréciés.
                  </p>

                  <div className="mt-2 flex items-center gap-x-3">
                    {preview ? (
                      <img
                        alt="Profil"
                        src={preview}
                        className="inline-block h-14 w-14 rounded-full object-cover"
                      />
                    ) : (
                      <UserCircleIcon aria-hidden="true" className="h-12 w-12 text-gray-300" />
                    )}

                    <input
                      onChange={onUploadPhoto}
                      type="file"
                      accept="image/png, image/jpeg, image/jpg"
                      className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    />

                    {uploadingPhoto && (
                      <span className="text-sm text-gray-500">Chargement…</span>
                    )}
                  </div>
                </div>

                <div className="col-span-full space-y-4">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Cochez ce qui vous correspond
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.checked)}
                    />
                    J'utilise WhatsApp pour échanger avec mes clients
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={visio}
                      onChange={(e) => setVisio(e.target.checked)}
                    />
                    Je fais mes rendez-vous en visio conférence
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={signature}
                      onChange={(e) => setSignature(e.target.checked)}
                    />
                    J'utilise la signature électronique
                  </label>
                </div>

                <div className="col-span-full space-y-3">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Vos domaines de compétences
                  </label>

                  {[
                    "Droit de l'immobilier",
                    "Succession",
                    "Fiscalité & gestion de patrimoine",
                    "Droit de l'entreprise",
                    "Droit de la famille",
                  ].map((item) => (
                    <label key={item} className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        value={item}
                        checked={competences.includes(item)}
                        onChange={onCheckCompetence}
                      />
                      {item}
                    </label>
                  ))}
                </div>

                <div className="col-span-full space-y-3">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Vos spécialités
                  </label>

                  {[
                    "Immobilier",
                    "Succession / Donation",
                    "Droit de l'entreprise",
                    "Fiscalité & gestion de patrimoine",
                    "Institutionnel",
                    "Droit de la famille",
                  ].map((item) => (
                    <label key={item} className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        value={item}
                        checked={specialites.includes(item)}
                        onChange={onCheckSpecialite}
                      />
                      {item}
                    </label>
                  ))}
                </div>

                <div className="col-span-full space-y-3">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Langues parlées
                  </label>

                  {[
                    { value: "Français", label: "🇫🇷 Français" },
                    { value: "Anglais", label: "🇬🇧 Anglais" },
                    { value: "Espagnol", label: "🇪🇸 Espagnol" },
                    { value: "Allemand", label: "🇩🇪 Allemand" },
                    { value: "Italien", label: "🇮🇹 Italien" },
                    { value: "Arabe", label: "🇸🇦 Arabe" },
                    { value: "Chinois", label: "🇨🇳 Chinois" },
                    { value: "Portugais", label: "🇵🇹 Portugais" },
                    { value: "Japonais", label: "🇯🇵 Japonais" },
                    { value: "Turc", label: "🇹🇷 Turc" },
                    { value: "Russe", label: "🇷🇺 Russe" },
                    { value: "Tamoul", label: "🇮🇳 Tamoul" },
                  ].map(({ value, label }) => (
                    <label key={value} className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        value={value}
                        checked={langues.includes(value)}
                        onChange={onCheckLangue}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
              <button
                type="submit"
                disabled={isSubmitting || uploadingPhoto}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {isSubmitting ? "Enregistrement..." : "Continuer"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}

export default OnBoardFicheNotaire;